import React, { Fragment } from 'react';
import { View } from 'react-native';
import { styled } from 'dripsy';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { useNavigation, useRoute } from '@react-navigation/native';

import {
  HorizontalTabView,
  HorizontalList,
  TutorialItem,
  Separator,
  emptyResult,
  CourseItem,
} from '@components';
import {
  useAsyncInit,
  useHorizontalTabView,
  useEffectOnFocus,
} from '@hooks';
import { AnalyticsService, ToastService } from '@services';
import { parseApiError } from '@utils';
import {
  ItemTypeEnum,
  TutorialExtraCategoryIdEnum,
} from '@constants';
import {
  HomeTabNavigatorNavigationPropType,
  HomeTabNavigatorRoutePropType,
  screens,
} from '@navigation';
import { ProgressCard } from './components/ProgressCard';
import { useViewerModel } from '@models/Viewer/ViewerModel';
import { useSectionsModel } from '@models/Sections/SectionsModel';
import type { Section } from '@models/Sections/SectionModel';
import { TutorialCourseItemType } from '@models/Sections/TutorialCourseUnionType';

const Container = styled(View)({
  flex: 1,
  backgroundColor: '$background0',
});

const ProgressCardContainer = styled(View)({
  px: ['$6', '$7', '5%'],
});

type NavigationPropType = HomeTabNavigatorNavigationPropType<'MyHub'>;
type RoutePropType = HomeTabNavigatorRoutePropType<'MyHub'>;

export const MyHubScreen = observer(() => {
  const nav = useNavigation<NavigationPropType>();
  const route = useRoute<RoutePropType>();
  const { t } = useTranslation();
  const sections = useSectionsModel();
  const { tutorialItemWidth } = useHorizontalTabView();
  const viewer = useViewerModel();

  useAsyncInit(() => {
    fetch();
    fetchUserWatchProgress();
  });

  // Silent update on focus (without UI such a loader indication)
  useEffectOnFocus(async () => {
    try {
      const promises = [];

      if (!sections.myHub.fetch.inProgress) {
        promises.push(sections.myHub.fetch.run());
      }

      if (!sections.myHub.fetch.inProgress) {
        promises.push(viewer.userWatchProgress.fetch.run());
      }

      if (!promises.length) {
        return;
      }

      await Promise.all(promises);
    } catch (error) {
      // Do nothing
    }
  });

  async function fetchUserWatchProgress() {
    try {
      await viewer.userWatchProgress.fetch.run();
    } catch (error) {
      // Do nothing
    }
  }

  async function fetch() {
    try {
      await sections.myHub.fetch.run();
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  }

  async function refresh() {
    try {
      await sections.myHub.refresh.run();
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  }

  function navigateToItem(
    item: TutorialCourseItemType,
    extraCategoryId?: TutorialExtraCategoryIdEnum,
  ) {
    // using there 'push' to prevent cashing tutorial screen
    // navigate to course screen if courseId is present
    // navigate to course summary screen if is course entity
    if (item.schema === ItemTypeEnum.Course) {
      nav.push(screens.HubNavigator, {
        screen: screens.CourseSummary,
        params: { id: item.data.id },
      });
      return;
    }

    if (item.schema === ItemTypeEnum.Tutorial && item.data.courseId) {
      nav.push(screens.HubNavigator, {
        screen: screens.Tutorial,
        params: {
          id: item.data.id,
          courseId: item.data.courseId,
        },
      });
    } else {
      nav.push(screens.HubNavigator, {
        screen: screens.Tutorial,
        params: { id: item.data.id, extraCategoryId },
      });
    }

    AnalyticsService.trackHomeContent({
      contentTitle: item.data.title,
    });
  }

  function renderTitleByType(section: Section) {
    return t('items.title', {
      context: section.sectionType,
    });
  }

  function getEmptyResultTypeBySectionType(section: Section) {
    return emptyResult.getTypeByItemListType(section.sectionType);
  }

  function navigateToSeeMoreBySectionType(section: Section) {
    nav.push(screens.HubNavigator, {
      screen: screens.Items,
      params: {
        type: section.sectionType,
      },
    });
  }

  const isLoading =
    sections.myHub.fetch.inProgress &&
    !sections.myHub.fetch.hasEverBeenRun;

  const isRefreshing = sections.myHub.refresh.inProgress;

  // sections cannot be empty
  const isError = sections.myHub.isEmpty;

  return (
    <Container>
      <HorizontalTabView
        routeName={route.name}
        isLoading={isLoading}
        isRefreshing={isRefreshing}
        isError={isError}
        onRefresh={refresh}
      >
        <Separator size="$6" />
        <ProgressCardContainer>
          <ProgressCard />
        </ProgressCardContainer>
        <Separator size="$7" />
        {sections.myHub.asArray.map(
          (myHubSection, index, myHubSections) => {
            const isNotLastHorizontalList =
              myHubSections.length - 1 !== index;

            return (
              <Fragment key={myHubSection.name + index}>
                <HorizontalList
                  title={renderTitleByType(myHubSection)}
                  data={
                    myHubSection.preview as TutorialCourseItemType[]
                  }
                  onMorePress={() => {
                    navigateToSeeMoreBySectionType(myHubSection);
                  }}
                  renderItem={({ item }) =>
                    item.schema === ItemTypeEnum.Tutorial ? (
                      <TutorialItem
                        item={item.data}
                        width={tutorialItemWidth}
                        onPress={() => navigateToItem(item)}
                      />
                    ) : (
                      <CourseItem
                        item={item.data}
                        width={tutorialItemWidth}
                        onPress={() => navigateToItem(item)}
                      />
                    )
                  }
                  keyExtractor={(i) =>
                    `${i.schema}-${i.data.id.toString()}`
                  }
                  ItemSeparatorComponent={() => (
                    <Separator size={['$6', '$8']} vertical />
                  )}
                  emptyResultType={getEmptyResultTypeBySectionType(
                    myHubSection,
                  )}
                />

                {isNotLastHorizontalList && <Separator size="$9" />}
              </Fragment>
            );
          },
        )}
        <Separator />
      </HorizontalTabView>
    </Container>
  );
});
